import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import PageSeo from "../components/PageSeo";
import TurnstileField from "../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../turnstileConfig";
import { isApiConfigured, postForgotPassword } from "../apiClient";
import Nd1AuthShell from "../nd1/components/Nd1AuthShell";
import Nd1ForgotPasswordMarketing from "../nd1/components/Nd1ForgotPasswordMarketing";
import "../nd1/nd1.css";

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    const normalized = String(email || "").trim().toLowerCase();
    if (!normalized) {
      setSubmitError("Enter your email address.");
      return;
    }

    if (!isApiConfigured()) {
      setSubmitError("The app is not connected to the API. Password reset is unavailable in this mode.");
      return;
    }

    setSubmitting(true);
    try {
      if (isTurnstileEnabled() && !turnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && turnstileToken ? String(turnstileToken) : undefined;
      await postForgotPassword({ email: normalized, turnstileToken: turnstilePayload });
      setDone(true);
      setTurnstileKey((k) => k + 1);
      setTurnstileToken(null);
    } catch (err) {
      let raw = err instanceof Error ? err.message : "Something went wrong";
      if (!raw || raw === "Something went wrong") {
        raw = "Something went wrong. Please try again.";
      }
      if (raw === "Failed to fetch" || /networkerror|load failed|fetch/i.test(raw)) {
        raw = "Could not reach the server. Check your connection and try again.";
      }
      setSubmitError(formatTurnstileErrorForUser(raw));
      setTurnstileKey((k) => k + 1);
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  const showTurnstile = isTurnstileEnabled() && isApiConfigured();

  const formCard = (
    <section className="nd1-auth-card nd1-auth-card--signin">
      <Link className="nd1-auth-back" to="/signin">
        <ArrowLeft className="nd1-icon" aria-hidden="true" />
        Back to sign in
      </Link>
      <p className="nd1-auth-card__eyebrow">Forgot password</p>
      {done ? (
        <>
          <p className="nd1-auth-card__lede">
            If an account exists for that email, we sent instructions to reset your password. Check
            your inbox and spam folder.
          </p>
          <p className="nd1-auth-verify-help">
            <Link className="nd1-auth-link-inline" to="/signin">
              Return to sign in
            </Link>
          </p>
        </>
      ) : (
        <>
          <form
            id="jobuwin-forgot-password-form"
            className="nd1-auth-form"
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <label htmlFor="forgotEmail">Email address</label>
            <div className="nd1-auth-field">
              <input
                id="forgotEmail"
                name="forgotEmail"
                type="email"
                autoComplete="username"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {!showTurnstile && submitError ? (
              <p className="nd1-auth-alert" role="alert">
                {submitError}
              </p>
            ) : null}

            {!showTurnstile ? (
              <button
                type="submit"
                className="nd1-btn nd1-btn--primary nd1-auth-submit"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send reset link"}
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </button>
            ) : null}
          </form>

          {showTurnstile ? (
            <div className="nd1-signup-captcha">
              {submitError === TURNSTILE_REQUIRED_MESSAGE ? (
                <p className="nd1-auth-alert" role="alert">
                  {submitError}
                </p>
              ) : null}
              <TurnstileField
                tokenSessionId={0}
                onTokenChange={(token) => {
                  setTurnstileToken(token);
                  if (token) {
                    setSubmitError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
                  }
                }}
                widgetKey={`forgot-password-${turnstileKey}`}
                options={{ action: "forgot-password" }}
              />
              <p className="nd1-turnstile-note">
                Security check: making sure you are a genuine human :-)
              </p>
              {submitError && submitError !== TURNSTILE_REQUIRED_MESSAGE ? (
                <p className="nd1-auth-alert" role="alert">
                  {submitError}
                </p>
              ) : null}
              <button
                type="submit"
                form="jobuwin-forgot-password-form"
                className="nd1-btn nd1-btn--primary nd1-auth-submit"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send reset link"}
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );

  return (
    <Nd1AuthShell hideFooter>
      <PageSeo
        title="Forgot password"
        description="Reset your JOBUWIN password securely and get back to genuine hiring conversations."
        path="/forgot-password"
        canonicalPath="/forgot-password"
        noindex
      />
      <main id="nd1-main" className="nd1-shell nd1-auth-shell nd1-auth-shell--signin">
        <div className="nd1-auth-panel">
          <Nd1ForgotPasswordMarketing />
          {formCard}
        </div>
      </main>
    </Nd1AuthShell>
  );
}

export default ForgotPasswordPage;
